import React, { useEffect, useRef, useState } from "react";
import { api, apiErr } from "@/lib/api";
import { mediaUrl } from "@/lib/media";
import { toast } from "sonner";
import { Loader2, Plus, Building2, Trash2, ImagePlus, UserPlus, KeyRound, ShieldOff, ShieldCheck, FolderKanban, X, Save } from "lucide-react";
import { L, SaveBtn, Card, SectionTitle, genPassword, fmtDate } from "./shared";
import NewClientUserDialog from "./NewClientUserDialog";

const EMPTY_C = { name: "", cui: "", reg_com: "", address: "", website: "", contact_name: "", contact_email: "", contact_phone: "", notes: "" };

export default function ClientsManager({ projects, onChanged }) {
  const [companies, setCompanies] = useState(null);
  const [sel, setSel] = useState(null);       // company id
  const [creating, setCreating] = useState(false);
  const [cf, setCf] = useState(EMPTY_C);
  const [saving, setSaving] = useState(false);
  const [users, setUsers] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [projUser, setProjUser] = useState(null); // user whose project access we edit
  const logoRef = useRef(null);

  async function load(keep = true) {
    try {
      const { data } = await api.get("/staff/client-companies"); setCompanies(data);
      if (!keep || !data.find(c => c.id === sel)) setSel(data[0]?.id || null);
    } catch (e) { toast.error(apiErr(e)); }
  }
  useEffect(() => { load(false); /* eslint-disable-next-line */ }, []);

  useEffect(() => {
    if (!sel || !companies) { setUsers([]); return; }
    const c = companies.find(x => x.id === sel); if (c) setCf({ ...EMPTY_C, ...c });
    api.get("/staff/client-users", { params: { company_id: sel } }).then(r => setUsers(r.data)).catch(() => setUsers([]));
  }, [sel, companies]);

  async function saveCompany() {
    if (!cf.name.trim()) { toast.error("Numele companiei este obligatoriu"); return; }
    setSaving(true);
    try {
      if (creating) { const { data } = await api.post("/staff/client-companies", cf); setCreating(false); await load(); setSel(data.id); toast.success("Client creat"); }
      else { await api.put(`/staff/client-companies/${sel}`, cf); await load(); toast.success("Client salvat"); }
      onChanged && onChanged();
    } catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
  }
  async function deleteCompany(c) {
    if (!window.confirm(`Ștergi clientul „${c.name}”? (posibil doar fără utilizatori și proiecte asociate)`)) return;
    try { await api.delete(`/staff/client-companies/${c.id}`); await load(false); onChanged && onChanged(); toast.success("Client șters"); }
    catch (e) { toast.error(apiErr(e)); }
  }
  async function uploadLogo(file) {
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try { await api.post(`/staff/client-companies/${sel}/logo`, fd, { headers: { "Content-Type": "multipart/form-data" } }); await load(); onChanged && onChanged(); toast.success("Logo actualizat"); }
    catch (e) { toast.error(apiErr(e)); } finally { if (logoRef.current) logoRef.current.value = ""; }
  }
  async function removeLogo() {
    try { await api.delete(`/staff/client-companies/${sel}/logo`); await load(); onChanged && onChanged(); }
    catch (e) { toast.error(apiErr(e)); }
  }
  async function refreshUsers() { const r = await api.get("/staff/client-users", { params: { company_id: sel } }); setUsers(r.data); await load(); onChanged && onChanged(); }
  async function resetPwd(u) {
    const pwd = genPassword();
    if (!window.confirm(`Resetezi parola pentru ${u.email}?\nParola temporară: ${pwd}`)) return;
    try { await api.post(`/staff/client-users/${u.id}/reset-password`, { password: pwd }); navigator.clipboard?.writeText(pwd); toast.success("Parolă resetată (copiată)"); }
    catch (e) { toast.error(apiErr(e)); }
  }
  async function toggleActive(u) {
    try { await api.put(`/staff/client-users/${u.id}`, { is_active: u.is_active === false }); await refreshUsers(); }
    catch (e) { toast.error(apiErr(e)); }
  }
  async function deleteUser(u) {
    if (!window.confirm(`Ștergi definitiv contul ${u.email}? Va pierde accesul la toate proiectele.`)) return;
    try { await api.delete(`/staff/client-users/${u.id}`); await refreshUsers(); toast.success("Cont șters"); }
    catch (e) { toast.error(apiErr(e)); }
  }

  if (!companies) return <div className="flex items-center gap-2 text-ink-muted"><Loader2 className="h-5 w-5 animate-spin" /> Se încarcă...</div>;
  const company = companies.find(c => c.id === sel);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[300px_1fr] gap-6" data-testid="clients-manager">
      <Card className="p-3">
        <div className="flex items-center justify-between px-2 mb-2">
          <span className="font-mono text-[10px] uppercase tracking-widest text-ink-muted">Clienți ({companies.length})</span>
          <button onClick={() => { setCreating(true); setCf(EMPTY_C); }} data-testid="cm-new-company" className="inline-flex items-center gap-1 text-[12px] text-brand hover:underline"><Plus className="h-3.5 w-3.5" /> Client nou</button>
        </div>
        <div className="space-y-0.5 max-h-[70vh] overflow-y-auto">
          {companies.map(c => (
            <button key={c.id} onClick={() => { setCreating(false); setSel(c.id); }} data-testid={`cm-company-${c.id}`}
              className={`w-full text-left px-3 py-2.5 rounded-lg flex items-center gap-3 ${sel === c.id && !creating ? "bg-ink text-white" : "hover:bg-neutral-100"}`}>
              <div className={`h-8 w-8 rounded-md flex items-center justify-center shrink-0 overflow-hidden ${sel === c.id && !creating ? "bg-white/10" : "bg-brand/10 text-brand"}`}>
                {c.logo_url ? <img src={mediaUrl(c.logo_url)} alt="" className="h-full w-full object-contain" /> : <Building2 className="h-4 w-4" />}
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-[13px] font-medium truncate">{c.name}</div>
                <div className={`text-[11px] ${sel === c.id && !creating ? "text-white/70" : "text-ink-muted"}`}>{c.users_count} utilizatori · {c.projects_count} proiecte</div>
              </div>
            </button>
          ))}
          {companies.length === 0 && <div className="text-[12px] text-ink-muted px-3 py-6 text-center">Niciun client. Creează primul.</div>}
        </div>
      </Card>

      <div className="space-y-6">
        {(creating || company) && (
          <Card data-testid="cm-company-form">
            <div className="flex items-start gap-4 mb-4">
              {!creating && (
                <div className="shrink-0">
                  <div className="h-20 w-20 rounded-xl border border-neutral-200 bg-neutral-50 flex items-center justify-center overflow-hidden">
                    {company?.logo_url ? <img src={mediaUrl(company.logo_url)} alt="logo" className="h-full w-full object-contain p-1" /> : <Building2 className="h-7 w-7 text-ink-muted" />}
                  </div>
                  <input ref={logoRef} type="file" accept="image/*" className="hidden" onChange={e => uploadLogo(e.target.files?.[0])} data-testid="cm-logo-input" />
                  <div className="flex gap-1 mt-1.5">
                    <button onClick={() => logoRef.current?.click()} className="text-[11px] text-brand hover:underline inline-flex items-center gap-1" data-testid="cm-logo-upload"><ImagePlus className="h-3 w-3" /> Logo</button>
                    {company?.logo_url && <button onClick={removeLogo} className="text-[11px] text-red-600 hover:underline">Șterge</button>}
                  </div>
                </div>
              )}
              <div className="flex-1">
                <SectionTitle sub={creating ? "Compania beneficiară — sub ea se creează utilizatorii-client." : `Creat ${fmtDate(company?.created_at)}`}>{creating ? "Client nou" : company?.name}</SectionTitle>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <L label="Denumire companie"><input value={cf.name} onChange={e => setCf({ ...cf, name: e.target.value })} data-testid="cm-name" className="inp" /></L>
                  <L label="CUI"><input value={cf.cui} onChange={e => setCf({ ...cf, cui: e.target.value })} className="inp" data-testid="cm-cui" /></L>
                  <L label="Nr. Reg. Com."><input value={cf.reg_com} onChange={e => setCf({ ...cf, reg_com: e.target.value })} className="inp" /></L>
                  <L label="Website"><input value={cf.website} onChange={e => setCf({ ...cf, website: e.target.value })} className="inp" /></L>
                  <div className="md:col-span-2"><L label="Adresă sediu"><input value={cf.address} onChange={e => setCf({ ...cf, address: e.target.value })} className="inp" /></L></div>
                  <L label="Persoană de contact"><input value={cf.contact_name} onChange={e => setCf({ ...cf, contact_name: e.target.value })} className="inp" /></L>
                  <L label="Email contact"><input value={cf.contact_email} onChange={e => setCf({ ...cf, contact_email: e.target.value })} className="inp" /></L>
                  <L label="Telefon contact"><input value={cf.contact_phone} onChange={e => setCf({ ...cf, contact_phone: e.target.value })} className="inp" /></L>
                  <L label="Note interne"><input value={cf.notes} onChange={e => setCf({ ...cf, notes: e.target.value })} className="inp" /></L>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <SaveBtn onClick={saveCompany} saving={saving} label={creating ? "Creează client" : "Salvează"} testid="cm-save" />
              {creating && <button onClick={() => setCreating(false)} className="text-sm text-ink-muted hover:text-ink">Anulează</button>}
              {!creating && company && <button onClick={() => deleteCompany(company)} className="ml-auto inline-flex items-center gap-1.5 text-[12px] text-ink-muted hover:text-red-600" data-testid="cm-delete"><Trash2 className="h-3.5 w-3.5" /> Șterge client</button>}
            </div>
          </Card>
        )}

        {!creating && company && (
          <Card data-testid="cm-users">
            <div className="flex items-start justify-between gap-3 mb-3">
              <SectionTitle sub="Fiecare utilizator vede DOAR proiectele bifate în coloana „Proiecte”.">Utilizatori ({users.length})</SectionTitle>
              <button onClick={() => setShowNew(true)} data-testid="cm-new-user" className="inline-flex items-center gap-2 px-3 py-2 bg-ink text-white text-[13px] rounded-lg hover:bg-brand whitespace-nowrap"><UserPlus className="h-4 w-4" /> Utilizator nou</button>
            </div>
            {users.length === 0 ? <div className="py-8 text-center text-ink-muted text-sm border border-dashed border-neutral-200 rounded-lg">Niciun utilizator.</div> : (
              <div className="divide-y divide-neutral-100 border border-neutral-100 rounded-lg overflow-hidden">
                {users.map(u => (
                  <div key={u.id} className={`flex items-center gap-3 px-4 py-3 ${u.is_active === false ? "opacity-60" : ""}`} data-testid={`cm-user-${u.email}`}>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-ink flex items-center gap-2 flex-wrap">{u.name}
                        {u.job_title && <span className="text-[11px] text-ink-muted font-normal">· {u.job_title}</span>}
                        {u.is_active === false && <span className="text-[10px] font-mono uppercase bg-red-50 text-red-600 px-1.5 rounded">dezactivat</span>}
                        {u.must_change_password && <span className="text-[10px] font-mono uppercase bg-amber-50 text-amber-700 px-1.5 rounded">onboarding</span>}
                      </div>
                      <div className="text-[12px] text-ink-muted">{u.email}{u.phone ? ` · ${u.phone}` : ""} · ultima logare {u.last_login ? fmtDate(u.last_login) : "niciodată"}</div>
                    </div>
                    <button onClick={() => setProjUser(u)} className="inline-flex items-center gap-1.5 px-2.5 py-1.5 border border-neutral-200 rounded-lg text-[12px] hover:border-ink" data-testid={`cm-projects-${u.email}`}>
                      <FolderKanban className="h-3.5 w-3.5" /> Proiecte ({(u.project_ids || []).length})
                    </button>
                    <button onClick={() => resetPwd(u)} title="Resetează parola" className="p-2 text-ink-muted hover:text-ink"><KeyRound className="h-4 w-4" /></button>
                    <button onClick={() => toggleActive(u)} title={u.is_active === false ? "Reactivează" : "Dezactivează"} className="p-2 text-ink-muted hover:text-ink">{u.is_active === false ? <ShieldCheck className="h-4 w-4" /> : <ShieldOff className="h-4 w-4" />}</button>
                    <button onClick={() => deleteUser(u)} title="Șterge cont" className="p-2 text-ink-muted hover:text-red-600" data-testid={`cm-del-${u.email}`}><Trash2 className="h-4 w-4" /></button>
                  </div>
                ))}
              </div>
            )}
          </Card>
        )}
      </div>

      {showNew && (
        <NewClientUserDialog companies={companies} defaultCompanyId={sel} projects={projects}
          onClose={() => setShowNew(false)} onCreated={refreshUsers} />
      )}
      {projUser && <UserProjectsDialog user={projUser} projects={projects} onClose={() => setProjUser(null)} onSaved={refreshUsers} />}
    </div>
  );
}

function UserProjectsDialog({ user, projects, onClose, onSaved }) {
  const [ids, setIds] = useState(new Set(user.project_ids || []));
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const toggle = (id) => setIds(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  async function save() {
    setBusy(true);
    try { await api.put(`/staff/client-users/${user.id}/projects`, { project_ids: [...ids] }); toast.success("Acces actualizat"); onSaved && onSaved(); onClose(); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  }
  const list = projects.filter(p => !q || `${p.name} ${p.code}`.toLowerCase().includes(q.toLowerCase()));
  return (
    <div className="fixed inset-0 z-[120] bg-ink/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl max-w-lg w-full p-6" onClick={e => e.stopPropagation()} data-testid="cm-projects-dialog">
        <div className="flex items-start justify-between mb-1">
          <div className="text-[18px] font-display font-semibold text-ink">Proiecte vizibile pentru {user.name}</div>
          <button onClick={onClose} className="text-ink-muted hover:text-ink"><X className="h-5 w-5" /></button>
        </div>
        <p className="text-[12px] text-ink-muted mb-3">Utilizatorul va vedea în portal exclusiv proiectele bifate. La adăugare primește notificare + email.</p>
        <input value={q} onChange={e => setQ(e.target.value)} className="inp mb-2" placeholder="Caută proiect..." />
        <div className="max-h-72 overflow-y-auto border border-neutral-100 rounded-lg divide-y divide-neutral-100">
          {list.map(p => (
            <label key={p.id} className="flex items-center gap-2 px-3 py-2 text-[13px] cursor-pointer hover:bg-neutral-50">
              <input type="checkbox" checked={ids.has(p.id)} onChange={() => toggle(p.id)} className="h-4 w-4 accent-[#111]" data-testid={`cm-proj-${p.code}`} />
              <span className="flex-1 truncate">{p.name}</span><span className="font-mono text-[11px] text-ink-muted">{p.code}</span>
            </label>
          ))}
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <span className="text-[12px] text-ink-muted mr-auto self-center">{ids.size} selectate</span>
          <button onClick={onClose} className="px-4 py-2 text-sm text-ink-muted hover:text-ink">Anulează</button>
          <button onClick={save} disabled={busy} data-testid="cm-projects-save" className="inline-flex items-center gap-2 px-5 py-2 bg-ink text-white text-sm rounded-lg hover:bg-brand disabled:opacity-60">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Salvează</button>
        </div>
      </div>
    </div>
  );
}
